import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  TextField,
  Typography,
  IconButton,
  Button,
  MenuItem,
  Autocomplete,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { Delete } from "@mui/icons-material";
import RequiredLabel from "../../shared/RequiredLabel";
import ConfirmDeleteDialog from "../../shared/ConfirmDeleteDialog";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { Tooltip } from "@mui/material";
import { LevelTooltipText, levelMap } from "../../shared/Constants";

const SkillExperience = ({
  skills = [],
  onChange,
  onAdd,
  onRemove,
  isRequired = false,
  error = false,
  duplicateSkillNames = [],
  endpoint = "/skill_autocomplete",
}) => {
  const [skillOptions, setSkillOptions] = useState([]);

  // 削除確認ダイアログの状態
  const [deleteTargetIndex, setDeleteTargetIndex] = useState(null);

  // levelMap(オブジェクト) → 配列オプションに変換
  const levelOptions = Object.entries(levelMap)
    .map(([value, label]) => ({ label, value: Number(value) }))
    .sort((a, b) => b.value - a.value);

  useEffect(() => {
    fetch(endpoint)
      .then((res) => res.json())
      .then((data) => setSkillOptions(data))
      .catch((err) => console.error("スキル一覧取得エラー:", err));
  }, [endpoint]);

  const updateSkill = (index, updates) => {
    const updated = [...skills];
    updated[index] = { ...updated[index], ...updates };
    onChange(updated);
  };

  const handleSkillSelect = (index, selected) => {
    // 手動で文字列を入力した場合
    if (typeof selected === "string") {
      updateSkill(index, { skill_name: selected, skill_id: null });

      // 選択肢（オブジェクト）から選んだ場合
    } else if (selected && selected.name) {
      updateSkill(index, { skill_name: selected.name, skill_id: selected.id });

      // 何も選択されていない、または選択をクリアした場合
    } else {
      updateSkill(index, { skill_name: "", skill_id: null });
    }
  };

  const handleDeleteConfirm = (index) => {
    // スキル名、経験年数、レベルのいずれかが入力されている場合は削除確認ダイアログを表示
    const skill = skills[index];
    const hasContent =
      (skill.skill_id !== null && skill.skill_id !== undefined) ||
      (skill.skill_name && skill.skill_name.trim() !== "") ||
      (skill.experience_year && String(skill.experience_year).trim() !== "") ||
      (skill.level && String(skill.level).trim() !== "");

    if (hasContent) {
      setDeleteTargetIndex(index);
    } else {
      onRemove(index);
    }
  };

  return (
    <Box>
      {/* 見出しと補足説明 */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Typography sx={{ fontSize: 14 }}>スキル</Typography>
        <RequiredLabel show={isRequired} />
      </Box>

      <LevelTooltipText
        fontSize={12}
        color="text.secondary"
        direction="row"
        sx={{ mb: 1 }}
      />

      {skills.map((skill, index) => (
        <Grid
          container
          spacing={2}
          alignItems="center"
          key={index}
          sx={{ mb: 2 }}
        >
          {/* スキル名 */}
          <Grid item xs={4}>
            <Autocomplete
              options={skillOptions.filter((opt) => {
                const selectedIds = skills
                  .map((s) => s.skill_id)
                  .filter((id) => id !== null);
                return !selectedIds.includes(opt.id);
              })}
              getOptionLabel={(option) => option.name ?? ""}
              value={
                skill.skill_id != null
                  ? skillOptions.find((s) => s.id === skill.skill_id) ?? null
                  : skill.skill_name?.trim()
                  ? { name: skill.skill_name }
                  : null
              }
              onChange={(event, newValue) => handleSkillSelect(index, newValue)}
              onInputChange={(event, newInputValue, reason) => {
                if (reason === "input") {
                  handleSkillSelect(index, newInputValue);
                }
              }}
              filterOptions={(options, state) => {
                // 候補を入力値でフィルター
                if (!state.inputValue) return options;
                return options.filter((opt) =>
                  opt.name
                    ?.toLowerCase()
                    .includes(state.inputValue.toLowerCase())
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="スキル名"
                  error={
                    error ||
                    duplicateSkillNames.includes(skill.skill_name?.trim())
                  }
                  fullWidth
                  onBlur={(event) => {
                    // フォーカスアウト時に手動入力値を確定
                    const inputValue = event.target.value?.trim();
                    if (inputValue && inputValue !== skill.skill_name) {
                      handleSkillSelect(index, inputValue);
                    }
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root input": {
                      border: "none",
                      boxShadow: "none",
                    },
                  }}
                />
              )}
              freeSolo
            />
          </Grid>

          {/* 年数 */}
          <Grid item xs={2}>
            <TextField
              fullWidth
              placeholder="経験年数"
              type="number"
              inputProps={{ min: 0 }}
              value={skill.experience_year ?? ""}
              onChange={(e) => {
                const val = e.target.value;
                if (val.length <= 3) {
                  updateSkill(index, { experience_year: val });
                }
              }}
              sx={{
                "& .MuiOutlinedInput-root input": {
                  border: "none",
                  boxShadow: "none",
                },
              }}
            />
          </Grid>

          <Grid item xs={1}>
            <Typography sx={{ mt: 3 }}>年以上</Typography>
          </Grid>

          {/* レベル */}
          <Grid item xs={2}>
            <TextField
              select
              fullWidth
              value={skill.level ?? ""}
              onChange={(e) =>
                updateSkill(index, { level: Number(e.target.value) })
              }
              SelectProps={{
                displayEmpty: true,
                renderValue: (selected) => {
                  if (selected === "") {
                    return <span style={{ color: "#9e9e9e" }}>レベル</span>;
                  }
                  const level = levelOptions.find(
                    (lvl) => lvl.value === selected
                  );
                  return level ? level.label : "";
                },
              }}
            >
              <MenuItem value="" disabled>
                レベル
              </MenuItem>
              {levelOptions.map((lvl) => (
                <MenuItem key={lvl.value} value={lvl.value}>
                  {lvl.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* 表示チェック & 削除 */}
          <Grid item xs={3}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: 2,
              }}
            >
              {/* 一覧に表示 */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!skill.visible_flag}
                    onChange={(e) =>
                      updateSkill(index, { visible_flag: e.target.checked })
                    }
                    sx={{ p: 0.5 }}
                  />
                }
                label={
                  <Typography fontSize={14} whiteSpace="nowrap">
                    一覧に表示
                  </Typography>
                }
                sx={{ marginRight: 0 }}
              />

              {/* ヘルプアイコン */}
              {index === 0 ? (
                <Tooltip
                  title="チェックを入れると、スキルが一覧画面に表示されます"
                  placement="top"
                  arrow
                >
                  <HelpOutlineIcon fontSize="small" color="action" />
                </Tooltip>
              ) : (
                <HelpOutlineIcon
                  fontSize="small"
                  sx={{ visibility: "hidden" }}
                />
              )}

              {/* 削除ボタン */}
              <IconButton
                color="error"
                onClick={() => handleDeleteConfirm(index)}
                disabled={skills.length === 1}
              >
                <Delete />
              </IconButton>
            </Box>
          </Grid>

          {/* 重複エラー */}
          {duplicateSkillNames.includes(skill.skill_name?.trim()) && (
            <Grid item xs={12} sx={{ mt: -2 }}>
              <Typography variant="caption" color="error">
                同じスキル名が入力されています
              </Typography>
            </Grid>
          )}
        </Grid>
      ))}

      <Button
        variant="contained"
        onClick={onAdd}
        sx={{
          backgroundColor: "#818cf8",
          fontWeight: "bold",
          "&:hover": { backgroundColor: "#6366f1" },
        }}
      >
        ＋スキルを追加する
      </Button>

      {/* 削除確認ダイアログ */}
      <ConfirmDeleteDialog
        open={deleteTargetIndex !== null}
        onClose={() => setDeleteTargetIndex(null)}
        onConfirm={() => {
          if (deleteTargetIndex !== null) {
            onRemove(deleteTargetIndex);
            setDeleteTargetIndex(null);
          }
        }}
      />
    </Box>
  );
};

export default SkillExperience;
